import {
  buildProviderSelectionHelp,
  describeProviderAvailability,
  detectProviders,
  fetchAvailableModelsForProvider,
  findProjectRoot,
  formatAvailableModelsOutput,
  getProviderDefinition,
  loadProjectEnv,
} from '../utils/models.js';

export interface AvailableModelsOptions {
  provider?: string;
}

function writeError(message: string): never {
  process.stderr.write(`${message}\n`);
  process.exit(1);
}

export async function availableModels(options: AvailableModelsOptions = {}): Promise<void> {
  const projectRoot = findProjectRoot(process.cwd());
  const env = loadProjectEnv(projectRoot);
  const detectedProviders = detectProviders(projectRoot, env);

  if (detectedProviders.length === 0) {
    writeError(
      [
        `No installed provider packages were found in ${projectRoot}.`,
        'Add a provider package such as `@standardagents/cerebras`, then run this command again.',
      ].join('\n')
    );
  }

  const providerName = options.provider?.trim();

  if (providerName) {
    const provider = detectedProviders.find((candidate) => candidate.name === providerName);

    if (!getProviderDefinition(providerName)) {
      writeError(
        `Unknown provider "${providerName}". Supported providers: ${detectedProviders.map((candidate) => candidate.name).join(', ')}.`
      );
    }

    if (!provider) {
      writeError(
        [
          `Provider "${providerName}" is not installed in ${projectRoot}.`,
          `Install ${getProviderDefinition(providerName)?.packageName || providerName} and try again.`,
        ].join('\n')
      );
    }

    if (!provider.configured) {
      writeError(
        [
          `Provider "${providerName}" is missing required environment variables.`,
          `Missing: ${provider.missingEnvKeys.join(', ')}`,
        ].join('\n')
      );
    }

    try {
      const models = await fetchAvailableModelsForProvider(provider.name, projectRoot, env);
      process.stdout.write(formatAvailableModelsOutput(provider, models));
      return;
    } catch (error) {
      const message = error instanceof Error ? error.message : `Failed to fetch models for ${providerName}.`;
      writeError(message);
    }
  }

  const configuredProviders = detectedProviders.filter((provider) => provider.configured);

  if (configuredProviders.length === 0) {
    const missingSummary = detectedProviders
      .map((provider) => describeProviderAvailability(provider))
      .join('\n');

    writeError(
      [
        'No installed providers are fully configured yet.',
        missingSummary,
      ].join('\n')
    );
  }

  if (configuredProviders.length > 1) {
    writeError(buildProviderSelectionHelp(configuredProviders));
  }

  const provider = configuredProviders[0];

  try {
    const models = await fetchAvailableModelsForProvider(provider.name, projectRoot, env);
    process.stdout.write(formatAvailableModelsOutput(provider, models));
  } catch (error) {
    const message = error instanceof Error ? error.message : `Failed to fetch models for ${provider.name}.`;
    writeError(message);
  }
}
